class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.loading = false;
    this.resultsOpen = false;
    this.rawQuery = '';
    this.resultsHTML = null;
    this.resources = this.dataset.resources?.split(',') || ['product'];
    this.selectedElement = null;
    this.activeElement = null;
    this.allOptionsArray = null;
    this._currentResultsGroup = '';
    this.inputEl = null;
    this.resultsEl = null;
    this.resultsSlot = null;
    this.groupToggles = null;
    this.searchLoadingElement = null;
    this.viewAllButton = null;
  }

  connectedCallback() {
    this.initRefs();
    this.bindEvents();
    this.setInitialResultsGroup();
  }

  get currentResultsGroup() {
    return this._currentResultsGroup;
  }

  set currentResultsGroup(newValue) {

    if (newValue === '') {
      this._currentResultsGroup = newValue;
      return;
    }

    if (this._currentResultsGroup === '') {
      if (this.querySelector(`[data-${newValue}-results]`)) {
        this.querySelector(`[data-${newValue}-results]`).hidden = false;
      }

      this._currentResultsGroup = newValue;
      return;
    }

    const currentResultsGroupEl =
      this.querySelector(`[data-${this._currentResultsGroup}-results]`);

    this._currentResultsGroup = newValue;

    const nextResultsGroupEl = this.querySelector(`[data-${newValue}-results]`);

    this.switchResultsGroup(currentResultsGroupEl, nextResultsGroupEl);
  }

  get trimmedQuery() {
    return this.rawQuery.trim();
  }

  get shouldAnimate() {
    return window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
  }

  get queryKey() {
    return this.trimmedQuery.replace(' ', '-').toLowerCase();
  }


  initRefs() {
    this.inputEl = this.querySelector('[data-search-input]');
    this.resultsEl = this.querySelector('[data-search-results]');
    this.resultsSlot = this.querySelector('[data-results-slot]');
    this.groupToggles = this.querySelector('[data-group-toggles]');
    this.searchLoadingElement = this.querySelector('[data-search-loading]');
    this.viewAllButton = this.querySelector('#predictive-search-option-search-keywords');
  }

  bindEvents() {
    if (this.inputEl) {
      this.inputEl.addEventListener(
      'input',
      this.debounce((e) => {
        this.rawQuery = e.target.value;
        this.onQueryChanged();
      }, 300)
      );
    }
  }

  debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  onQueryChanged() {
    if (!this.trimmedQuery.length) {
      this.resultsHTML = null;
      this.updateResultsVisibility();
    } else {
      this.getSearchResults();
    }
  }

  updateResultsVisibility() {
    if (this.resultsHTML) {
      this.resultsEl.hidden = false;
      this.resultsEl.innerHTML = this.resultsHTML;
      this.viewAllButton?.classList.remove('hidden');
      this.setInitialResultsGroup();
      this.resultsSlot?.scrollTo(0, 0);
      this.allOptionsArray = Array.from(this.querySelectorAll('[role="option"]'));
      this.updateGroupToggles();
    } else {
      this.resultsEl.hidden = true;
      this.viewAllButton?.classList.add('hidden');
    }
  }

  async getSearchResults() {
    this.searchLoadingElement?.classList.remove('opacity-0', 'invisible', 'hidden');

    const updatedHTML = await fetchHTML(
      getURLWithParams(window.theme.routes.predictive_search_url, {
        q: this.trimmedQuery,
        'resources[type]': this.resources,
        'resources[limit_scope]': 'each',
        section_id: 'predictive-search-new',
      })
    );

    const resultsMarkup = querySelectorInHTMLString(
        '#shopify-section-predictive-search-new',
        updatedHTML
      ).innerHTML;

    const liveRegionText = querySelectorInHTMLString(
      '#predictive-search-count',
      updatedHTML
    ).innerHTML;

    this.resultsHTML = resultsMarkup;
    this.updateResultsVisibility();

    liveRegion(liveRegionText);

    this.allOptionsArray = Array.from(
      this.querySelectorAll('[role="option"]')
    );

    document.dispatchEvent(
      new CustomEvent('switch:load:images', {
        detail: { container: this.resultsEl },
      })
    );

    this.searchLoadingElement?.classList.add('opacity-0', 'invisible', 'hidden');
    if (this.searchLoadingElement) {
      this.searchLoadingElement.hidden = true;
    }
  }

  handleEscape() {
    const toggle = Array.from(document.querySelectorAll('[data-search-open]')).find((el) => el.offsetParent);
    this.closeResults(true);
    toggle?.focus();
  }

  setInitialResultsGroup() {
    if (this.querySelector('[data-group-toggles]')) {
      let initialGroupInput = this.querySelector('[data-group-toggles] input[type="radio"]');
      this.currentResultsGroup = initialGroupInput?.value || 'products';
      if (initialGroupInput) initialGroupInput.checked = true;
    } else {
      this.currentResultsGroup = 'products';
    }
  }

  updateGroupToggles() {
    this.groupToggles = this.querySelector('[data-group-toggles]');
    this.groupToggles?.addEventListener('change', (e) => {
      const newGroup = e.target.value;
      this.currentResultsGroup = newGroup;
    });
  }

  switchResultsGroup(fromEl, toEl) {
    fromEl?.setAttribute('hidden', '');
    toEl?.removeAttribute('hidden');
  }
}

customElements.define('predictive-search', PredictiveSearch);
